import React from 'react';
import PropTypes from 'prop-types';
import './Badge.scss';

/**
 * Design System Badge Component
 *
 * A gaming-inspired badge component for displaying achievements, levels, and status indicators.
 * Supports earned/locked states, different sizes, and custom icons.
 *
 * @example
 * <Badge
 *   title="5 Years Veteran"
 *   icon="trophy"
 *   earned={true}
 *   description="Been with the company for 5+ years"
 * />
 */
const Badge = ({
    title,
    icon,
    earned = false,
    locked = false,
    size = 'medium',
    variant = 'hexagon',
    description,
    onClick,
    className = '',
    style = {},
    count,
    ...props
}) => {
    const [isHovered, setIsHovered] = React.useState(false);

    const baseClassName = 'ds-badge';
    const variantClassName = `${baseClassName}--${variant}`;
    const sizeClassName = `${baseClassName}--${size}`;
    const earnedClassName = earned ? `${baseClassName}--earned` : '';
    const lockedClassName = locked ? `${baseClassName}--locked` : '';
    const clickableClassName = onClick ? `${baseClassName}--clickable` : '';

    const combinedClassName = [
        baseClassName,
        variantClassName,
        sizeClassName,
        earnedClassName,
        lockedClassName,
        clickableClassName,
        className
    ].filter(Boolean).join(' ');

    const handleClick = (e) => {
        if (onClick && !locked) {
            onClick(e);
        }
    };

    // Handle icon - can be Font Awesome name, SVG path, or React element
    const iconElement = typeof icon === 'string' ? (
        icon.startsWith('/') || icon.includes('.svg') ? (
            <img src={icon} alt={title} className="badge-medal-image" />
        ) : (
            <i className={`fa fa-${icon}`} />
        )
    ) : (
        icon
    );

    return (
        <div
            className={combinedClassName}
            style={style}
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            title={description}
            {...props}
        >
            <div className="ds-badge__container">
                <div className="ds-badge__icon-wrapper">
                    {iconElement}
                    {locked && (
                        <div className="ds-badge__lock-overlay">
                            <i className="fa fa-lock" />
                        </div>
                    )}
                </div>
                {count !== undefined && (
                    <div className="ds-badge__count">
                        {count}
                    </div>
                )}
            </div>
            {title && (
                <div className="ds-badge__title">{title}</div>
            )}
            {isHovered && description && (
                <div className="ds-badge__tooltip">
                    {description}
                </div>
            )}
        </div>
    );
};

Badge.propTypes = {
    /** Badge title displayed below the icon */
    title: PropTypes.string,

    /** Icon - can be Font Awesome name (string) or React element */
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

    /** Whether the badge has been earned */
    earned: PropTypes.bool,

    /** Whether the badge is locked (not yet achievable) */
    locked: PropTypes.bool,

    /** Badge size */
    size: PropTypes.oneOf(['small', 'medium', 'large']),

    /** Badge shape variant */
    variant: PropTypes.oneOf(['hexagon', 'circle', 'shield', 'square']),

    /** Tooltip description shown on hover */
    description: PropTypes.string,

    /** Click handler */
    onClick: PropTypes.func,

    /** Additional CSS classes */
    className: PropTypes.string,

    /** Inline styles */
    style: PropTypes.object,

    /** Count badge number (e.g., number of times earned) */
    count: PropTypes.number
};

export default Badge;
